import { LeadFromSearch } from "lib/hooks/api/leads/useLeads";
import { LeadWorkflow } from "lib/hooks/api/leads/useLeadWorkflow";

export type BoardStage = {
  id: string;
  name: string;
  color: string;
  iconColor: string;
  iconName: string;
};

export type StageColumn = {
  stage: BoardStage;
  leads: LeadFromSearch[];
};

/**
 * Board columns mirror the active stage filter: a stage the user has filtered
 * out gets no column, because it could never hold a card.
 * An empty, null or undefined filter means "no stage filter applied" — show
 * every stage. This matches app-service, which only applies the filter when
 * stageIds is non-empty, so an empty array returns leads from every stage.
 */
export function selectBoardStages(
  workflow: LeadWorkflow | undefined,
  filterStageIds: string[] | null | undefined,
): BoardStage[] {
  if (!workflow) return [];
  const allowed = filterStageIds?.length ? new Set(filterStageIds) : null;
  return [...workflow.transitions]
    .sort((a, b) => a.order - b.order)
    .filter((t) => allowed === null || allowed.has(t.stage.id))
    .map((t) => ({
      id: t.stage.id,
      name: t.stage.name,
      color: t.stage.color,
      iconColor: t.stage.iconColor,
      iconName: t.stage.iconName,
    }));
}

export function groupLeadsByStage(
  leads: LeadFromSearch[],
  stages: BoardStage[],
  overrides: Record<string, string>,
): StageColumn[] {
  const buckets = new Map<string, LeadFromSearch[]>(stages.map((s) => [s.id, []]));
  leads.forEach((lead) => {
    const stageId = overrides[lead.id] ?? lead.stage.id;
    const bucket = buckets.get(stageId);
    if (!bucket) return;
    // A just-moved card goes to the top of its new column instead of keeping its
    // place in the fetched order. Columns render the leads search in whatever sort
    // the table is set to — last updated first, by default — and moving a lead bumps
    // its updatedAt, so the top is where the next refetch puts it anyway. Landing it
    // mid-column instead makes it jump the moment that data arrives.
    if (stageId === lead.stage.id) bucket.push(lead);
    else bucket.unshift(lead);
  });
  return stages.map((stage) => ({ stage, leads: buckets.get(stage.id) ?? [] }));
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const SOON_THRESHOLD_DAYS = 5;

export function getDueDate(
  expiryDate: string | null | undefined,
  now: Date,
): { label: string; soon: boolean } | null {
  if (!expiryDate) return null;
  const days = Math.ceil((new Date(expiryDate).getTime() - now.getTime()) / MS_PER_DAY);
  if (days < 0) return { label: "Expired", soon: true };
  return {
    label: `Expiring in ${days} ${days === 1 ? "day" : "days"}`,
    soon: days <= SOON_THRESHOLD_DAYS,
  };
}

/**
 * Whether a hovered column should grow a placeholder slot for the card in flight.
 * A card already sitting in this column would be a no-op move — onDragEnd ignores
 * those — so the column must not pretend to make room for it.
 */
export function shouldShowDropPlaceholder(
  isOver: boolean,
  activeLeadId: string | null,
  columnLeadIds: string[],
): boolean {
  if (!isOver || !activeLeadId) return false;
  return !columnLeadIds.includes(activeLeadId);
}

export type StageMove = { leadId: string; targetStageId: string };

/**
 * The stage change a finished drag implies, or null when it was a no-op: released
 * outside any column, released over the column it started in, or for a lead the board
 * no longer holds. Split out from the drag handler because the id mapping is the part
 * that can silently break — dnd-kit gives ids, not leads, and a wrong one just stops
 * persisting rather than throwing.
 */
export function resolveStageMove(
  activeId: string | number | null | undefined,
  overId: string | number | null | undefined,
  leads: LeadFromSearch[],
  overrides: Record<string, string>,
): StageMove | null {
  if (activeId == null || overId == null) return null;

  const leadId = String(activeId);
  const targetStageId = String(overId);
  const lead = leads.find((l) => l.id === leadId);
  // The override wins: after an optimistic move the lead's own stage is already stale.
  const currentStageId = overrides[leadId] ?? lead?.stage.id;

  if (!currentStageId || currentStageId === targetStageId) return null;
  return { leadId, targetStageId };
}
